import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Popover from "@radix-ui/react-popover";
import Check from "lucide-react/dist/esm/icons/check";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import LogOut from "lucide-react/dist/esm/icons/log-out";

import { ANALYTICS_EVENTS } from "@/constants/analytics.constants";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/i18n";
import { posthog } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { useUser } from "@/context/user-provider";

import { Avatar } from "@/components/base";

import { ConfirmDialog } from "./Dialog";

export const UserMenu: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, userLoading, logout } = useUser();
  const [open, setOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const { fullName, email } = user ?? {};

  const initials = fullName || email || "?";

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={t("Open user menu")}
            className={cn(
              "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2",
              "hover:bg-surface-hovered",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
            )}
          >
            <Avatar size="sm" initials={initials} />
            <div className="flex min-w-0 flex-1 flex-col text-left">
              <span className="text-text truncate text-[14px] leading-[16px] font-medium">
                {userLoading ? t("Loading...") : fullName}
              </span>
              {email && (
                <span className="text-subdued truncate text-[11px] leading-[16px]">
                  {email}
                </span>
              )}
            </div>
            <ChevronDown
              className={cn(
                "text-subdued h-4 w-4 shrink-0 transition-transform",
                open && "-rotate-90"
              )}
            />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="right"
            align="end"
            sideOffset={6}
            className={cn(
              "app-root animate-overlay-in z-50 w-[260px] overflow-hidden",
              "border-border rounded-lg border",
              "bg-card shadow-popover"
            )}
          >
            <div className="py-1">
              <button
                onClick={() => setLanguageOpen((prev) => !prev)}
                className="hover:bg-surface-hovered flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-2 text-left text-[13px]"
              >
                <span>{t("Language")}</span>
                <span className="text-subdued flex items-center gap-1 text-xs">
                  {SUPPORTED_LANGUAGES[i18n.language as SupportedLanguage] ??
                    i18n.language}
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 transition-transform",
                      languageOpen && "rotate-180"
                    )}
                  />
                </span>
              </button>
              {languageOpen && (
                <div className="border-border-subdued bg-surface-subdued border-t py-1">
                  {(
                    Object.entries(SUPPORTED_LANGUAGES) as [
                      SupportedLanguage,
                      string,
                    ][]
                  ).map(([code, label]) => (
                    <button
                      key={code}
                      onClick={() => {
                        void i18n.changeLanguage(code);
                        setLanguageOpen(false);
                      }}
                      className="hover:bg-surface-hovered flex w-full cursor-pointer items-center justify-between px-6 py-1.5 text-left text-[13px]"
                    >
                      <span>{label}</span>
                      {i18n.language === code && (
                        <Check className="text-primary h-3.5 w-3.5" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              <a
                href="https://www.konko.ai/terms"
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  posthog.capture(ANALYTICS_EVENTS.TERMS_AND_CONDITIONS_VIEWED)
                }
                className="text-text hover:bg-surface-hovered flex items-center justify-between gap-2 px-4 py-2 text-[13px]"
              >
                <span>{t("Terms and conditions")}</span>
                <ExternalLink className="text-subdued h-3.5 w-3.5" />
              </a>
            </div>
            <div className="border-border border-t py-1">
              <button
                onClick={() => {
                  setOpen(false);
                  setConfirmLogout(true);
                }}
                className="text-destructive hover:bg-destructive-background flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-[13px]"
              >
                <LogOut className="h-4 w-4" />
                <span>{t("Log out")}</span>
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <ConfirmDialog
        open={confirmLogout}
        onOpenChange={setConfirmLogout}
        title={t("Log out of Konko?")}
        description={t("You'll need to sign in again to continue.")}
        confirmLabel={t("Log out")}
        tone="critical"
        onConfirm={() => {
          setConfirmLogout(false);
          logout();
        }}
      />
    </>
  );
};
